import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 };
const btn = { padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, marginLeft: 8 };
const btnPrimary = { ...btn, background: '#2563eb', color: '#fff', border: '1px solid #2563eb' };
const btnDanger = { ...btn, background: '#dc2626', color: '#fff', border: '1px solid #dc2626' };
const inp = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' };
const lbl = { fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 };
const chipBase = { padding: '5px 13px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, cursor: 'pointer', background: '#fff' };
const chipOn = { ...chipBase, background: '#dbeafe', border: '1px solid #2563eb', color: '#1e40af', fontWeight: 600 };
const warn = { background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#854d0e', marginTop: 12 };
const danger = { ...warn, background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b' };
const STEPS = ['Audience', 'Message', 'Preview', 'Schedule', 'Send'];

export default function MarketingWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [msg, setMsg] = useState('');
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [cap, setCap] = useState('ALL');
  const [customCap, setCustomCap] = useState('');
  const [tplId, setTplId] = useState('');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [campaignId, setCampaignId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState('');
  const [mode, setMode] = useState('plan');
  const [plan, setPlan] = useState({ dailyStartQty: 250, rampIntervalDays: 7, maxDailyQty: 5000, sendHour: 10, startDate: new Date().toISOString().slice(0, 10) });
  const [confirmName, setConfirmName] = useState('');
  const [largeOk, setLargeOk] = useState(false);
  const [launched, setLaunched] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await client.get('/admin/marketing/databases');
        const g = d.data.groups || d.data.savedGroups || d.data.saved_groups || [];
        setGroups(Array.isArray(g) ? g : []);
      } catch (e) { setMsg('Failed to load groups'); }
      try {
        const t = await client.get('/admin/marketing/templates');
        setTemplates(Array.isArray(t.data) ? t.data : (t.data.templates || []));
      } catch (e) { setMsg('Failed to load templates'); }
    })();
  }, []);

  const group = groups.find(g => String(g.id) === String(groupId));
  const groupTotal = group ? (group.member_count ?? group.memberCount ?? 0) : 0;
  const effCap = cap === 'ALL' ? null : (cap === 'CUSTOM' ? Number(customCap) || null : Number(cap));
  const targetCount = effCap ? Math.min(effCap, groupTotal) : groupTotal;
  const segment = groupId ? ('GROUP_' + groupId + (effCap ? ':' + effCap : '')) : '';
  const overCap = targetCount > 500;

  const pickTemplate = (id) => {
    setTplId(id);
    const t = templates.find(x => String(x.id) === String(id));
    if (t) { setSubject(t.subject || ''); setBodyHtml(t.body_html || t.bodyHtml || ''); setBodyText(t.body_text || t.bodyText || ''); setName((t.name || 'Campaign') + ' - ' + new Date().toISOString().slice(0, 10)); } /* WIZ_POLISH_v1 auto-name follows template */
  };

  const saveDraft = useCallback(async () => {
    const payload = { name, segment, subject, body_template: bodyHtml, channel: 'EMAIL' };
    if (campaignId) {
      await client.patch('/admin/marketing/campaigns/' + campaignId, payload);
      return campaignId;
    }
    const r = await client.post('/admin/marketing/campaigns', payload);
    setCampaignId(r.data.id);
    return r.data.id;
  }, [name, segment, subject, bodyHtml, campaignId]);

  const goPreview = async () => {
    setBusy(true); setMsg('');
    try {
      const id = await saveDraft();
      const p = await client.get('/admin/marketing/campaigns/' + id + '/render-preview');
      setPreview(p.data);
      setStep(2);
    } catch (e) { setMsg('Preview failed: ' + (e.response?.data?.message || e.response?.data || e.message)); }
    setBusy(false);
  };

  const sendTest = async () => {
    if (!testEmail) { setTestResult('Enter a test address first.'); return; }
    setBusy(true); setTestResult('');
    try {
      const r = await client.post('/admin/marketing/campaigns/' + campaignId + '/send?test=true&testEmail=' + encodeURIComponent(testEmail));
      setTestResult(r.data.result === 'sent' ? 'Test sent to ' + testEmail + ' - check the inbox.' : 'Test failed.');
    } catch (e) { setTestResult('Test failed: ' + (e.response?.data || e.message)); }
    setBusy(false);
  };

  const launch = async () => {
    setBusy(true); setMsg('');
    try {
      if (mode === 'plan') {
        const r = await client.post('/admin/marketing/send-plans', {
          name, groupId: Number(groupId), templateId: Number(tplId),
          startDate: plan.startDate, dailyStartQty: Number(plan.dailyStartQty),
          rampIntervalDays: Number(plan.rampIntervalDays), maxDailyQty: Number(plan.maxDailyQty), sendHour: Number(plan.sendHour),
        });
        setLaunched({ type: 'plan', id: r.data.id });
      } else {
        let url = '/admin/marketing/campaigns/' + campaignId + '/send?confirm=' + encodeURIComponent(confirmName);
        if (largeOk) url += '&allowLarge=true';
        const r = await client.post(url);
        setLaunched({ type: 'now', data: r.data });
      }
      setStep(5);
    } catch (e) {
      const d = e.response?.data;
      if (d && d.error === 'CAP_CONFIRM_REQUIRED') setMsg(d.message + ' Tick the confirmation box below and press Launch again.');
      else if (d && d.error === 'CONFIRM_REQUIRED') setMsg('Type the campaign name exactly: ' + (d.expected || name));
      else setMsg('Launch failed: ' + (d?.message || JSON.stringify(d) || e.message));
    }
    setBusy(false);
  };

  const canNext = [
    () => !!groupId,
    () => !!tplId && !!name && !!subject,
    () => true,
    () => mode !== 'now' || true,
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 20 }}>
      <h2 style={{ marginTop: 0 }}>New campaign</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {STEPS.map((s, i) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, background: i === step ? '#2563eb' : (i < step ? '#16a34a' : '#e5e7eb'), color: i <= step ? '#fff' : '#6b7280' }}>{i < step ? '\u2713' : i + 1}</span>
            <span style={{ fontSize: 13, fontWeight: i === step ? 600 : 400, color: i === step ? '#111' : '#6b7280' }}>{s}</span>
            {i < STEPS.length - 1 && <span style={{ width: 24, height: 1, background: '#e5e7eb' }} />}
          </div>
        ))}
      </div>

      {msg && <div style={warn}>{msg}</div>}

      {step === 0 && (
        <div style={card}>
          <span style={lbl}>Audience group</span>
          <select style={inp} value={groupId} onChange={e => setGroupId(e.target.value)}>
            <option value="">— choose a group —</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.member_count ?? g.memberCount ?? '?'})</option>)}
          </select>
          {group && (
            <div style={{ marginTop: 14 }}>
              <span style={lbl}>Send cap</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {['100', '500', '2000', 'ALL'].map(c => (
                  <span key={c} style={cap === c ? chipOn : chipBase} onClick={() => setCap(c)}>{c === 'ALL' ? 'All ' + groupTotal.toLocaleString() : c}</span>
                ))}
                <span style={cap === 'CUSTOM' ? chipOn : chipBase} onClick={() => setCap('CUSTOM')}>Custom</span>
                {cap === 'CUSTOM' && <input type="number" style={{ ...inp, width: 110 }} value={customCap} onChange={e => setCustomCap(e.target.value)} placeholder="count" />}
              </div>
              <div style={{ marginTop: 12, fontSize: 13, color: '#374151' }}>
                Target: <b>{targetCount.toLocaleString()}</b> contacts · suppressed and unsubscribed are excluded automatically at send time.
              </div>
              {overCap && <div style={warn}>Over the 500 safety cap — a second confirmation will be required at the Send step.</div>}
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div style={card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><span style={lbl}>Template</span>
              <select style={inp} value={tplId} onChange={e => pickTemplate(e.target.value)}>
                <option value="">— choose —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div><span style={lbl}>Campaign name (you will type this to launch)</span><input style={inp} value={name} onChange={e => setName(e.target.value)} /></div>
          </div>
          <div style={{ marginTop: 12 }}><span style={lbl}>Subject</span><input style={inp} value={subject} onChange={e => setSubject(e.target.value)} /></div>
          <div style={{ marginTop: 12 }}><span style={lbl}>Body (HTML — merge fields: {'{{first_name}} {{business_name}} {{activation_url}} {{unsubscribe_url}}'})</span>
            <textarea style={{ ...inp, height: 220, fontFamily: 'monospace', fontSize: 12 }} value={bodyHtml} onChange={e => setBodyHtml(e.target.value)} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div style={card}>
          {preview ? (
            <>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Rendered against a real contact: <b>{preview.previewContact}</b></div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>{preview.subject}</div>
                <div dangerouslySetInnerHTML={{ __html: preview.html || '' }} />
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input style={{ ...inp, width: 280 }} value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@yourcompany.com" />
                <button style={btn} disabled={busy} onClick={sendTest}>Send test</button>
                {testResult && <span style={{ fontSize: 13, color: '#374151' }}>{testResult}</span>}
              </div>
            </>
          ) : <div>Loading preview…</div>}
        </div>
      )}

      {step === 3 && (
        <div style={card}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div style={{ ...card, marginBottom: 0, cursor: 'pointer', border: mode === 'plan' ? '2px solid #2563eb' : card.border }} onClick={() => setMode('plan')}>
              <div style={{ fontWeight: 600 }}>New send plan (recommended)</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Daily ramp with automatic safety brakes. Runs unattended Mon–Fri.</div>
            </div>
            <div style={{ ...card, marginBottom: 0, cursor: 'pointer', border: mode === 'now' ? '2px solid #2563eb' : card.border }} onClick={() => setMode('now')}>
              <div style={{ fontWeight: 600 }}>Send now</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>One blast, right away. Over 500 needs extra confirmation.</div>
            </div>
          </div>
          {mode === 'plan' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 16 }}>
              <div><span style={lbl}>Start date</span><input type="date" style={inp} value={plan.startDate} onChange={e => setPlan(p => ({ ...p, startDate: e.target.value }))} /></div>
              <div><span style={lbl}>Start at (per day)</span><input type="number" style={inp} value={plan.dailyStartQty} onChange={e => setPlan(p => ({ ...p, dailyStartQty: e.target.value }))} /></div>
              <div><span style={lbl}>Double every (days)</span><input type="number" style={inp} value={plan.rampIntervalDays} onChange={e => setPlan(p => ({ ...p, rampIntervalDays: e.target.value }))} /></div>
              <div><span style={lbl}>Daily maximum</span><input type="number" style={inp} value={plan.maxDailyQty} onChange={e => setPlan(p => ({ ...p, maxDailyQty: e.target.value }))} /></div>
              <div><span style={lbl}>Send hour (CT)</span><input type="number" style={inp} value={plan.sendHour} onChange={e => setPlan(p => ({ ...p, sendHour: e.target.value }))} /></div>
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div style={card}>
          <table style={{ width: '100%', fontSize: 13 }}>
            <tbody>
              <tr><td style={{ color: '#6b7280', padding: '4px 0' }}>Group</td><td style={{ textAlign: 'right' }}>{group?.name}</td></tr>
              <tr><td style={{ color: '#6b7280', padding: '4px 0' }}>Recipients</td><td style={{ textAlign: 'right', fontWeight: 600, color: overCap ? '#dc2626' : '#111' }}>{mode === 'plan' ? groupTotal.toLocaleString() + ' (ramped daily)' : targetCount.toLocaleString()}</td></tr>
              <tr><td style={{ color: '#6b7280', padding: '4px 0' }}>Template</td><td style={{ textAlign: 'right' }}>{templates.find(t => String(t.id) === String(tplId))?.name}</td></tr>
              <tr><td style={{ color: '#6b7280', padding: '4px 0' }}>Mode</td><td style={{ textAlign: 'right' }}>{mode === 'plan' ? 'Send plan · ' + plan.dailyStartQty + '/day start, Mon–Fri ' + plan.sendHour + ':00' : 'Send now'}</td></tr>
            </tbody>
          </table>
          {mode === 'now' && (
            <>
              {overCap && (
                <div style={danger}>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input type="checkbox" checked={largeOk} onChange={e => setLargeOk(e.target.checked)} style={{ marginTop: 2 }} />
                    <span>This send targets more than 500 recipients and cannot be undone once started. I confirm the size is intentional.</span>
                  </label>
                </div>
              )}
              <div style={{ marginTop: 14 }}>
                <span style={lbl}>Type the campaign name exactly to arm the launch: <b>{name}</b></span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={inp} value={confirmName} onChange={e => setConfirmName(e.target.value)} placeholder={name} />
                  <button style={btn} onClick={() => setConfirmName(name)}>Fill it in for me</button>
                </div>
              </div>
            </>
          )}
          {mode === 'plan' && <div style={{ ...warn, background: '#dbeafe', border: '1px solid #93c5fd', color: '#1e40af' }}>The plan is created as a DRAFT. Nothing sends until you press Activate on the Send plans page — where prior recipients on this group are sealed into the ledger first.</div>}
        </div>
      )}

      {step === 5 && launched && (
        <div style={{ ...card, textAlign: 'center' }}>
          {launched.type === 'plan' ? (
            <>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Plan created as draft</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>Review it and press Activate when ready.</div>
              <button style={{ ...btnPrimary, marginTop: 14 }} onClick={() => nav('/send-plans')}>Go to Send plans</button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Campaign launched</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>{launched.data?.queued ?? ''} queued of {launched.data?.total ?? ''} — the worker sends in the background.</div>
              <button style={{ ...btnPrimary, marginTop: 14 }} onClick={() => nav('/marketing')}>Go to campaign table</button>
            </>
          )}
        </div>
      )}

      {step < 5 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <button style={btn} disabled={step === 0 || busy} onClick={() => setStep(s => s - 1)}>Back</button>
          {step < 4 && <button style={btnPrimary} disabled={busy || !canNext[step]()} onClick={() => (step === 1 ? goPreview() : setStep(s => s + 1))}>{busy ? 'Working…' : 'Continue'}</button>}
          {step === 4 && <button style={mode === 'now' && overCap && !largeOk ? btn : (mode === 'now' ? btnDanger : btnPrimary)} disabled={busy || (mode === 'now' && (!confirmName || (overCap && !largeOk)))} onClick={launch}>{busy ? 'Working…' : (mode === 'plan' ? 'Create plan' : 'Launch to ' + targetCount.toLocaleString())}</button>}
        </div>
      )}
    </div>
  );
}
